/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useContext, useRef } from "react";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
// types
import { GiftingCartState } from "@cart-client/reducers/GiftingCartReducer";
import { CartOverlaysContext } from "@swiggy-private/checkout-core";
import { Item } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard";
import { CroutonIds, CroutonPriority, GiftingCartItemType } from "@swiggy-private/cart-types";
// utils
import { isValidNumber, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { isAnyIMItem } from "@cart-client/helpers/GiftingIMSubCart";
// styles
import styles from "./GiftingCartError.scss";
// constants
import { GIFTING_CART_ERROR_CROUTON_HIDE_DELAY } from "./constants";

export const useGiftingCartErrorCrouton = () => {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const { cartState } = useContext(CartContext);
    const { reviewedCart } = cartState as GiftingCartState;
    const { registerCrouton, onCloseCrouton } = useContext(CartOverlaysContext);

    const hideItemQuantityErrorCrouton = useCallback(() => {
        timerRef.current = setTimeout(() => {
            onCloseCrouton();
        }, GIFTING_CART_ERROR_CROUTON_HIDE_DELAY);
    }, [onCloseCrouton]);

    const validateItemMaxQuantityAdditionAndRenderErrorCrouton = useCallback(
        (item: Item, title = "") => {
            // Early return if not an IM item
            if (!isAnyIMItem(item?.subType as GiftingCartItemType)) {
                return true;
            }

            const imCartItem = reviewedCart?.data?.imCartItems?.find(imItem => imItem.itemId === item.id);
            if (isEmpty(imCartItem)) {
                return true;
            }

            const { cart_allowed_quantity } = imCartItem?.metadata?.variations[0] || {};
            const { total = -1, message = "" } = cart_allowed_quantity || {};
            const finalTitle = title || message;

            // Early return if any validation fails
            const isValidTotal = isValidNumber(total) && total > 0;
            const isValidQuantity = isValidNumber(imCartItem.quantity);
            const hasExceededLimit = total <= imCartItem.quantity;
            const hasTitleMessage = !isEmpty(finalTitle);

            if (!isValidTotal || !isValidQuantity || !hasExceededLimit || !hasTitleMessage) {
                return true;
            }

            // Show error crouton if all validations pass
            registerCrouton({
                id: CroutonIds.GIFTING_CART_ERROR,
                priority: CroutonPriority.PRIORITY_ORDER_ONE,
                content: {
                    title: finalTitle,
                    titleClassName: styles.giftingCartErrorCroutonTitle,
                    wrapperClassName: styles.giftingCartErrorCroutonContainer,
                    leftIcon: {
                        iconName: "warningFilled20",
                        fillColor: "warning",
                        strokeColor: "warning",
                    },
                },
            });
            hideItemQuantityErrorCrouton();
            return false;
        },
        [hideItemQuantityErrorCrouton, registerCrouton, reviewedCart?.data?.imCartItems],
    );

    useEffectOnce(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    });

    return {
        validateItemMaxQuantityAdditionAndRenderErrorCrouton,
    };
};
